import { normalizeItemKeyword } from './textNormalize'
import { mergeQuantityMass } from './quantityMass'

export type MacroBucket = 'proteins' | 'carbs' | 'fats' | 'produce_micros'

export type IngredientEntry = {
  canonicalName: string
  macro: MacroBucket
  searchKeyword: string
  /** All tokens that must match in product name (prevents partial collisions). */
  requiredTokens: string[]
}

type RegistryRow = {
  aliases: string[]
  canonicalName: string
  macro: MacroBucket
  searchKeyword: string
  requiredTokens?: string[]
}

const REGISTRY: RegistryRow[] = [
  // Proteins
  {
    aliases: ['chicken breast', 'chicken breasts', 'grilled chicken', 'chicken thigh', 'chicken', 'hähnchenbrust', 'haehnchenbrust', 'hühnerbrust', 'huehnerbrust'],
    canonicalName: 'Chicken Breast',
    macro: 'proteins',
    searchKeyword: 'haehnchenbrust',
    requiredTokens: ['haehnchen', 'huhn', 'chicken'],
  },
  {
    aliases: ['beef strips', 'beef steak', 'beef mince', 'ground beef', 'beef', 'steak'],
    canonicalName: 'Beef',
    macro: 'proteins',
    searchKeyword: 'rindfleisch',
    requiredTokens: ['rind', 'beef'],
  },
  {
    aliases: ['lamb meat', 'lamb mince', 'lamb strips', 'lamb', 'lammfleisch'],
    canonicalName: 'Lamb',
    macro: 'proteins',
    searchKeyword: 'lammfleisch',
    requiredTokens: ['lamm', 'lamb'],
  },
  {
    aliases: ['ground turkey', 'turkey breast', 'turkey mince', 'turkey', 'putenbrust', 'pute', 'truthahn'],
    canonicalName: 'Turkey',
    macro: 'proteins',
    searchKeyword: 'putenbrust',
    requiredTokens: ['pute', 'turkey'],
  },
  {
    aliases: ['salmon fillet', 'salmon', 'wild salmon', 'smoked salmon', 'lachs', 'lachsfilet'],
    canonicalName: 'Salmon',
    macro: 'proteins',
    searchKeyword: 'lachs',
    requiredTokens: ['lachs', 'salmon'],
  },
  {
    aliases: ['eggs', 'egg', 'free range eggs', 'freilandeier', 'eier', 'ei'],
    canonicalName: 'Eggs',
    macro: 'proteins',
    searchKeyword: 'eier',
    requiredTokens: ['eier', 'egg'],
  },
  {
    aliases: ['greek yogurt', 'greek yoghurt', 'yogurt', 'yoghurt', 'magertopfen', 'quark', 'low fat quark', 'joghurt', 'griechischer joghurt', 'naturjoghurt', 'skyr'],
    canonicalName: 'Greek Yogurt',
    macro: 'proteins',
    searchKeyword: 'magertopfen',
    requiredTokens: ['joghurt', 'yogurt', 'topfen', 'quark'],
  },
  {
    aliases: ['tofu', 'firm tofu'],
    canonicalName: 'Tofu',
    macro: 'proteins',
    searchKeyword: 'tofu',
    requiredTokens: ['tofu'],
  },
  {
    aliases: ['black beans', 'schwarze bohnen', 'black bean'],
    canonicalName: 'Black Beans',
    macro: 'proteins',
    searchKeyword: 'schwarze bohnen',
    requiredTokens: ['bohnen', 'beans', 'schwarze'],
  },
  {
    aliases: ['green beans', 'grüne bohnen', 'gruene bohnen', 'french beans', 'haricots verts'],
    canonicalName: 'Green Beans',
    macro: 'produce_micros',
    searchKeyword: 'gruene bohnen',
    requiredTokens: ['bohnen', 'beans'],
  },
  {
    aliases: ['oat milk', 'hafermilch', 'haferdrink', 'oat drink', 'hafer drink', 'hafer milch'],
    canonicalName: 'Oat Milk',
    macro: 'carbs',
    searchKeyword: 'hafermilch',
    requiredTokens: ['hafer', 'oat'],
  },
  {
    aliases: ['lentils', 'lentil', 'red lentils', 'linsen', 'rote linsen'],
    canonicalName: 'Lentils',
    macro: 'proteins',
    searchKeyword: 'linsen',
    requiredTokens: ['linsen', 'lentil'],
  },
  {
    aliases: ['chickpeas', 'chickpea'],
    canonicalName: 'Chickpeas',
    macro: 'proteins',
    searchKeyword: 'kichererbsen',
    requiredTokens: ['kichererbsen', 'chickpea'],
  },
  {
    aliases: ['prawns', 'shrimp', 'shrimps'],
    canonicalName: 'Prawns',
    macro: 'proteins',
    searchKeyword: 'garnelen',
    requiredTokens: ['garnele', 'shrimp', 'prawn'],
  },
  {
    aliases: ['protein powder', 'whey protein'],
    canonicalName: 'Protein Powder',
    macro: 'proteins',
    searchKeyword: 'proteinpulver',
    requiredTokens: ['protein'],
  },
  {
    aliases: ['pork tenderloin', 'pork loin', 'pork chop', 'pork chops', 'pork fillet', 'pork mince', 'ground pork', 'pork belly', 'pork', 'schweinefleisch', 'schweinefilet', 'schweinelende', 'schweineschnitzel', 'schwein'],
    canonicalName: 'Pork',
    macro: 'proteins',
    searchKeyword: 'schweinefleisch',
    requiredTokens: ['schwein', 'pork'],
  },
  {
    aliases: ['veal escalope', 'veal chop', 'veal mince', 'veal', 'kalbfleisch', 'kalbsschnitzel', 'kalbslende', 'kalb'],
    canonicalName: 'Veal',
    macro: 'proteins',
    searchKeyword: 'kalbfleisch',
    requiredTokens: ['kalb', 'veal'],
  },
  // Carbs
  {
    aliases: ['sweet potato', 'sweet potatoes', 'baked sweet potato', 'süßkartoffel', 'suesskartoffel', 'süsskartoffel', 'süsskartoffeln', 'yam'],
    canonicalName: 'Sweet Potato',
    macro: 'carbs',
    searchKeyword: 'suesskartoffel',
    requiredTokens: ['suesskartoffel', 'susskartoffel', 'sweet potato', 'kartoffel'],
  },
  {
    aliases: ['brown rice', 'white rice', 'rice', 'basmati rice'],
    canonicalName: 'Rice',
    macro: 'carbs',
    searchKeyword: 'reis',
    requiredTokens: ['reis', 'rice'],
  },
  {
    aliases: ['oats', 'oatmeal', 'rolled oats', 'protein oats', 'haferflocken'],
    canonicalName: 'Oats',
    macro: 'carbs',
    searchKeyword: 'haferflocken',
    requiredTokens: ['hafer', 'oat'],
  },
  {
    aliases: ['pasta', 'whole wheat pasta', 'spaghetti', 'penne'],
    canonicalName: 'Pasta',
    macro: 'carbs',
    searchKeyword: 'nudeln',
    requiredTokens: ['nudel', 'pasta', 'spaghetti'],
  },
  {
    aliases: ['bread', 'whole grain bread', 'sourdough'],
    canonicalName: 'Bread',
    macro: 'carbs',
    searchKeyword: 'brot',
    requiredTokens: ['brot', 'bread'],
  },
  {
    aliases: ['potato', 'potatoes', 'baby potatoes'],
    canonicalName: 'Potato',
    macro: 'carbs',
    searchKeyword: 'kartoffeln',
    requiredTokens: ['kartoffel', 'potato'],
  },
  {
    aliases: ['quinoa'],
    canonicalName: 'Quinoa',
    macro: 'carbs',
    searchKeyword: 'quinoa',
    requiredTokens: ['quinoa'],
  },
  {
    aliases: ['granola', 'muesli'],
    canonicalName: 'Granola',
    macro: 'carbs',
    searchKeyword: 'muesli',
    requiredTokens: ['muesli', 'granola'],
  },
  {
    aliases: ['couscous', 'cous cous', 'couscousse', 'couscous salat'],
    canonicalName: 'Couscous',
    macro: 'carbs',
    searchKeyword: 'couscous',
    requiredTokens: ['couscous'],
  },
  {
    aliases: ['polenta', 'maisgrieß', 'maisgries', 'instant polenta', 'cornmeal'],
    canonicalName: 'Polenta',
    macro: 'carbs',
    searchKeyword: 'polenta',
    requiredTokens: ['polenta', 'maisgries'],
  },
  {
    aliases: ['egg noodles', 'rice noodles', 'glass noodles', 'udon noodles', 'soba noodles', 'ramen noodles', 'noodles', 'mie noodles'],
    canonicalName: 'Noodles',
    macro: 'carbs',
    searchKeyword: 'nudeln',
    requiredTokens: ['nudel', 'noodle'],
  },
  {
    aliases: ['bulgur', 'bulgur wheat', 'bulgar'],
    canonicalName: 'Bulgur',
    macro: 'carbs',
    searchKeyword: 'bulgur',
    requiredTokens: ['bulgur'],
  },
  // Fats
  {
    aliases: ['almond butter', 'almond spread'],
    canonicalName: 'Almond Butter',
    macro: 'fats',
    searchKeyword: 'mandelbutter',
    requiredTokens: ['mandel', 'almond'],
  },
  {
    aliases: ['peanut butter'],
    canonicalName: 'Peanut Butter',
    macro: 'fats',
    searchKeyword: 'erdnussbutter',
    requiredTokens: ['erdnuss', 'peanut'],
  },
  {
    aliases: ['chia seeds', 'chia', 'chiasamen'],
    canonicalName: 'Chia Seeds',
    macro: 'fats',
    searchKeyword: 'chiasamen',
    requiredTokens: ['chia'],
  },
  {
    aliases: ['almonds', 'almond', 'raw almonds', 'mandeln', 'mandelkerne', 'mandel'],
    canonicalName: 'Almonds',
    macro: 'fats',
    searchKeyword: 'mandeln',
    requiredTokens: ['mandel', 'almond'],
  },
  {
    aliases: ['olive oil', 'extra virgin olive oil', 'olivenöl', 'olivenoel', 'olivenöl extra vergine'],
    canonicalName: 'Olive Oil',
    macro: 'fats',
    searchKeyword: 'olivenoel',
    requiredTokens: ['oliven', 'olive'],
  },
  {
    aliases: ['avocado', 'avocados'],
    canonicalName: 'Avocado',
    macro: 'fats',
    searchKeyword: 'avocado',
    requiredTokens: ['avocado'],
  },
  {
    aliases: ['butter', 'unsalted butter'],
    canonicalName: 'Butter',
    macro: 'fats',
    searchKeyword: 'butter',
    requiredTokens: ['butter'],
  },
  {
    aliases: ['cheese', 'feta cheese', 'cheddar', 'käse', 'kaese'],
    canonicalName: 'Cheese',
    macro: 'fats',
    searchKeyword: 'kaese',
    requiredTokens: ['kaese', 'käse', 'cheese'],
  },
  {
    aliases: ['walnuts', 'walnut', 'walnüsse', 'walnuesse', 'walnusskerne', 'walnuss'],
    canonicalName: 'Walnuts',
    macro: 'fats',
    searchKeyword: 'walnuss',
    requiredTokens: ['walnuss', 'walnut'],
  },
  {
    aliases: ['coconut oil'],
    canonicalName: 'Coconut Oil',
    macro: 'fats',
    searchKeyword: 'kokosoel',
    requiredTokens: ['kokos', 'coconut'],
  },
  // Produce / micros
  {
    aliases: ['broccoli', 'steamed broccoli', 'roasted broccoli', 'brokkoli'],
    canonicalName: 'Broccoli',
    macro: 'produce_micros',
    searchKeyword: 'brokkoli',
    requiredTokens: ['brokkoli', 'broccoli'],
  },
  {
    aliases: ['spinach', 'baby spinach', 'fresh spinach', 'spinat', 'spinat (frisch)', 'frischer spinat', 'blattspinat'],
    canonicalName: 'Spinach',
    macro: 'produce_micros',
    searchKeyword: 'spinat',
    requiredTokens: ['spinat', 'spinach'],
  },
  {
    aliases: ['mixed vegetables', 'vegetables', 'veggies', 'gemuese', 'gemüse', 'gemischtes gemüse', 'tiefkühlgemüse'],
    canonicalName: 'Mixed Vegetables',
    macro: 'produce_micros',
    searchKeyword: 'gemuese',
    requiredTokens: ['gemuese', 'gemuse', 'vegetable'],
  },
  {
    aliases: ['bell pepper', 'bell peppers', 'red pepper', 'capsicum', 'paprika', 'paprikaschote', 'paprikaschoten', 'peperoni'],
    canonicalName: 'Bell Pepper',
    macro: 'produce_micros',
    searchKeyword: 'paprika',
    requiredTokens: ['paprika', 'pepper'],
  },
  {
    aliases: ['blueberries', 'blueberry', 'heidelbeeren', 'heidelbeere', 'blaubeeren'],
    canonicalName: 'Blueberries',
    macro: 'produce_micros',
    searchKeyword: 'heidelbeeren',
    requiredTokens: ['heidelbeere', 'blaubeere', 'blueberr'],
  },
  {
    aliases: ['banana', 'bananas', 'banane', 'bananen'],
    canonicalName: 'Banana',
    macro: 'produce_micros',
    searchKeyword: 'banane',
    requiredTokens: ['banane', 'banana'],
  },
  {
    aliases: ['apple', 'apples', 'äpfel', 'apfel', 'apfeln'],
    canonicalName: 'Apple',
    macro: 'produce_micros',
    searchKeyword: 'apfel',
    requiredTokens: ['apfel', 'apple'],
  },
  {
    aliases: ['tomato', 'tomatoes', 'cherry tomatoes', 'tomate', 'tomaten', 'cherrytomaten'],
    canonicalName: 'Tomato',
    macro: 'produce_micros',
    searchKeyword: 'tomate',
    requiredTokens: ['tomate', 'tomato'],
  },
  {
    aliases: ['cucumber', 'cucumbers', 'gurke', 'gurken', 'salatgurke'],
    canonicalName: 'Cucumber',
    macro: 'produce_micros',
    searchKeyword: 'gurke',
    requiredTokens: ['gurke', 'cucumber'],
  },
  {
    aliases: ['lemon', 'lemons'],
    canonicalName: 'Lemon',
    macro: 'produce_micros',
    searchKeyword: 'zitrone',
    requiredTokens: ['zitrone', 'lemon'],
  },
  {
    aliases: ['garlic', 'knoblauch', 'knoblauchzehe', 'knoblauchzehen'],
    canonicalName: 'Garlic',
    macro: 'produce_micros',
    searchKeyword: 'knoblauch',
    requiredTokens: ['knoblauch', 'garlic'],
  },
  {
    aliases: ['onion', 'onions', 'red onion', 'zwiebel', 'zwiebeln', 'rote zwiebel', 'zwiebeln gemischt'],
    canonicalName: 'Onion',
    macro: 'produce_micros',
    searchKeyword: 'zwiebel',
    requiredTokens: ['zwiebel', 'onion'],
  },
  {
    aliases: ['mushrooms', 'mushroom', 'pilze', 'champignons', 'champignon', 'steinpilze', 'cremini'],
    canonicalName: 'Mushrooms',
    macro: 'produce_micros',
    searchKeyword: 'champignons',
    requiredTokens: ['champignon', 'pilz', 'mushroom'],
  },
  {
    aliases: ['mango', 'mangos', 'mangoes', 'frozen mango', 'gefrorene mango', 'tiefkühl mango'],
    canonicalName: 'Mango',
    macro: 'produce_micros',
    searchKeyword: 'mango',
    requiredTokens: ['mango'],
  },
  {
    aliases: ['honey', 'honig', 'roher honig', 'manukahonig'],
    canonicalName: 'Honey',
    macro: 'produce_micros',
    searchKeyword: 'honig',
    requiredTokens: ['honig', 'honey'],
  },
  {
    aliases: ['milk', 'whole milk', 'skim milk', 'skimmed milk', 'milch', 'vollmilch', 'magermilch'],
    canonicalName: 'Milk',
    macro: 'produce_micros',
    searchKeyword: 'milch',
    requiredTokens: ['milch', 'milk'],
  },
  {
    aliases: ['zucchini', 'courgette', 'zucchetti', 'zucchine'],
    canonicalName: 'Zucchini',
    macro: 'produce_micros',
    searchKeyword: 'zucchini',
    requiredTokens: ['zucchini', 'courgette'],
  },
  {
    aliases: ['carrot', 'carrots', 'karotte', 'karotten', 'möhre', 'moehre', 'möhren', 'moehren'],
    canonicalName: 'Carrot',
    macro: 'produce_micros',
    searchKeyword: 'karotte',
    requiredTokens: ['karotte', 'carrot', 'mohre'],
  },
  {
    aliases: ['leek', 'leeks', 'porree', 'lauch'],
    canonicalName: 'Leek',
    macro: 'produce_micros',
    searchKeyword: 'porree',
    requiredTokens: ['porree', 'lauch', 'leek'],
  },
  {
    aliases: ['sweet corn', 'corn', 'sweetcorn', 'corn on the cob', 'mais', 'zuckermais'],
    canonicalName: 'Sweet Corn',
    macro: 'produce_micros',
    searchKeyword: 'zuckermais',
    requiredTokens: ['mais', 'corn'],
  },
  {
    aliases: ['peas', 'pea', 'erbsen', 'tiefkühlerbsen', 'tiefkuehlerbsen', 'green peas'],
    canonicalName: 'Peas',
    macro: 'produce_micros',
    searchKeyword: 'erbsen',
    requiredTokens: ['erbsen', 'pea'],
  },
  {
    aliases: ['asparagus', 'spargel', 'green asparagus', 'white asparagus', 'grüner spargel'],
    canonicalName: 'Asparagus',
    macro: 'produce_micros',
    searchKeyword: 'spargel',
    requiredTokens: ['spargel', 'asparagus'],
  },
  {
    aliases: ['soy sauce', 'soy', 'sojasouce', 'sojasoße', 'sojasosse', 'tamari'],
    canonicalName: 'Soy Sauce',
    macro: 'produce_micros',
    searchKeyword: 'sojasosse',
    requiredTokens: ['soja', 'soy'],
  },
  {
    aliases: ['cottage cheese', 'hüttenkäse', 'huettenkaese'],
    canonicalName: 'Cottage Cheese',
    macro: 'proteins',
    searchKeyword: 'huettenkaese',
    requiredTokens: ['hutten', 'cottage'],
  },
]

const aliasIndex = new Map<string, RegistryRow>()

for (const row of REGISTRY) {
  for (const alias of row.aliases) {
    aliasIndex.set(normalizeItemKeyword(alias), row)
  }
  aliasIndex.set(normalizeItemKeyword(row.canonicalName), row)
}

function stripPrepWords(text: string): string {
  return text
    .replace(/\b(fresh|raw|organic|bio|steamed|roasted|grilled|baked|chopped|diced|sliced|minced|ground|lean|low fat|low-fat|free range|wild|smoked|unsalted|extra virgin)\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function resolveIngredient(rawName: string): IngredientEntry {
  const stripped = stripPrepWords(rawName)
  const normalized = normalizeItemKeyword(stripped)

  let row = aliasIndex.get(normalized)
  if (!row) {
    for (const [alias, entry] of aliasIndex.entries()) {
      if (normalized.includes(alias) || alias.includes(normalized)) {
        row = entry
        break
      }
    }
  }

  if (row) {
    return {
      canonicalName: row.canonicalName,
      macro: row.macro,
      searchKeyword: row.searchKeyword,
      requiredTokens: row.requiredTokens ?? [row.searchKeyword],
    }
  }

  const fallbackMacro = inferMacro(normalized)
  const token = normalized.split(' ').filter((t) => t.length > 2)[0] ?? normalized
  return {
    canonicalName: titleCase(rawName.trim()),
    macro: fallbackMacro,
    searchKeyword: token,
    requiredTokens: [token],
  }
}

function inferMacro(normalized: string): MacroBucket {
  if (/chicken|beef|lamb|turkey|salmon|fish|egg|tofu|lentil|chickpea|prawn|shrimp|protein|meat|mince|fleisch|pork|veal|schwein|kalb|venison|boar|game/.test(normalized)) {
    return 'proteins'
  }
  if (/rice|oat|pasta|bread|potato|quinoa|granola|muesli|kartoffel|nudel|brot|hafer|noodle|couscous|polenta|bulgur|barley|buckwheat|corn|mais|cereal/.test(normalized)) {
    return 'carbs'
  }
  if (/oil|butter|avocado|almond|nut|cheese|walnut|kokos|oliven|mandel|erdnuss|seed|tahini|flax|lein/.test(normalized)) {
    return 'fats'
  }
  return 'produce_micros'
}

function titleCase(value: string): string {
  return value.replace(/\b\w/g, (c) => c.toUpperCase())
}

export function macroBucketLabel(bucket: MacroBucket): string {
  const labels: Record<MacroBucket, string> = {
    proteins: 'PROTEIN',
    carbs: 'CARBS',
    fats: 'FATS',
    produce_micros: 'PRODUCE / MICROS',
  }
  return labels[bucket]
}

export function macroCategoryLegacy(bucket: MacroBucket): string {
  const map: Record<MacroBucket, string> = {
    proteins: 'PROTEIN',
    carbs: 'CARBS',
    fats: 'FATS',
    produce_micros: 'PRODUCE',
  }
  return map[bucket]
}

export type RawIngredient = { name: string; quantity: string }

export type AggregatedIngredient = {
  canonicalName: string
  quantity_mass: string
  macro: MacroBucket
  macro_category: string
}

export function aggregateRawIngredients(items: RawIngredient[]): AggregatedIngredient[] {
  const map = new Map<string, AggregatedIngredient>()

  for (const item of items) {
    const resolved = resolveIngredient(item.name)
    const key = `${resolved.macro}::${resolved.canonicalName.toLowerCase()}`
    const qty = item.quantity?.trim() || 'as needed'
    const prev = map.get(key)

    map.set(key, {
      canonicalName: resolved.canonicalName,
      macro: resolved.macro,
      macro_category: macroCategoryLegacy(resolved.macro),
      quantity_mass: prev ? mergeQuantityMass(prev.quantity_mass, qty) : qty,
    })
  }

  return [...map.values()]
}

export type GroupedShoppingList = Record<MacroBucket, AggregatedIngredient[]>

export function groupAggregatedIngredients(items: AggregatedIngredient[]): GroupedShoppingList {
  const grouped: GroupedShoppingList = {
    proteins: [],
    carbs: [],
    fats: [],
    produce_micros: [],
  }

  for (const item of items) {
    grouped[item.macro].push(item)
  }

  for (const bucket of Object.keys(grouped) as MacroBucket[]) {
    grouped[bucket].sort((a, b) => a.canonicalName.localeCompare(b.canonicalName))
  }

  return grouped
}

export const MACRO_BUCKET_ORDER: MacroBucket[] = ['proteins', 'carbs', 'fats', 'produce_micros']
